import { Box, Container, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../utils/i18n';
import useScrollReveal from '../../hooks/useScrollReveal';

/**
 * Final call-to-action section — motivational headline, subtitle, and
 * "Get Started" button on a gradient-accented card. Uses useScrollReveal
 * for fade-in animation. Accepts `id` prop for anchor scrolling.
 */
export default function CTASection({ id }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const ref = useScrollReveal();

  return (
    <Box id={id} sx={{ py: { xs: 8, md: 12 } }}>
      <Container maxWidth="md">
        <Box
          ref={ref}
          sx={{
            textAlign: 'center',
            px: { xs: 3, sm: 5, md: 8 },
            py: { xs: 6, md: 8 },
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
            // Gradient background
            background:
              'linear-gradient(135deg, rgba(108,99,255,0.15) 0%, rgba(0,217,166,0.12) 100%)',
            border: '1px solid rgba(108,99,255,0.2)',
          }}
        >
          {/* Headline */}
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem', lg: '2.25rem' },
            }}
          >
            {t('landing.cta.title')}
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: 4,
              maxWidth: 480,
              mx: 'auto',
              fontSize: { xs: '0.95rem', md: '1.05rem' },
              lineHeight: 1.6,
            }}
          >
            {t('landing.cta.subtitle')}
          </Typography>

          {/* CTA Button */}
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/dashboard')}
            sx={{
              px: 5,
              py: 1.5,
              fontSize: '1rem',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #6C63FF, #00D9A6)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a52e0, #00c494)',
              },
            }}
          >
            {t('landing.cta.button')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
